// @givmo/board — the Board Ask's page, as one director sees it (EMC-WEB4 over the backend's EMC-BE9;
// walkthrough frames 26 and 31), at `/director/asks/$askId?org=`: the address the ask's mail spells
// whole (`BOARD_DIRECTOR_ASK_PATH`). Change both sides in one round, never one.
//
// Everything the director needs on one page: the campaign, the message from whoever sent it with its
// files to download, and either way to help that the ask offers — the share kit (rented from
// `@givmo/org-console`'s `ShareKit`, which share-kit-m5 builds once for every sharing surface), and
// "Create your page" (the fundraiser page wizard rented from `@givmo/fundraisers-module`, which
// director-page-m5 builds). Once she has a page, it takes the place of both cards: "Your fundraiser
// page" with its progress, Edit and See, then "Share your page" with ONE link and ONE QR (the kit's
// link carries on: the same slug now points at her page).
//
// Opening the page records her opening, once. Sharing through the kit, or starting her page, records
// that act, once: either takes the ask off her to-do list. Nothing about anyone else is shown here.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoneyDisplay, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Meter,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { Download, FileText, Pencil } from 'lucide-react';
import {
  BOARD_ASK_COPY as w,
  boardAskFileSize,
  boardAskKeys,
  fetchDirectorBoardAsk,
  markDirectorBoardAskOpened,
  recordDirectorBoardAskAct,
  type DirectorBoardAsk,
} from '@givmo/org-console';
import { useParams } from '../../board-routing';
import { useAskSlots } from './ask-slots';
import { directorBoardIdentity, useDirectorBoardAddress } from '../../director/director-board';
import { isCommitteeOnly } from '../../director/director-wire';
import { useDirectorReadsMayFire } from '../../director/read-gate';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { DirectorShell } from './director-shell';

const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK = 'font-sans text-s14 font-w600 text-brand-600 hover:text-brand-700';
const BODY =
  'whitespace-pre-line font-sans text-s14 leading-h15 tracking-givmo text-text-secondary';

const DIRECTOR_WORDS = {
  pageTitle: 'Board Ask',
  asked: (from: string | null) => `${from ?? 'Your organization'} asked the board to help.`,
  seeCampaign: 'See the campaign page',
  from: (name: string | null) => (name ? `From ${name}` : 'From your board'),
  download: 'Download',
  createTitle: 'Or create your own fundraiser page',
  createTitleOnly: 'Create your own fundraiser page',
  createLine: 'Raise toward the campaign with a goal of your own.',
  createButton: 'Create your page',
  yourPage: 'Your fundraiser page',
  yourPageLine: 'Everything it raises counts toward the appeal.',
  finishPage: 'Finish your page',
  editPage: 'Edit your page',
  seePage: 'See your page',
  raisedOf: (goal: string | null, donors: number) =>
    `raised${goal ? ` of ${goal}` : ''} · ${donors} ${donors === 1 ? 'donor' : 'donors'}`,
  notFound: 'This Board Ask isn’t here. It may not have been sent to you.',
  loadFailed: 'We couldn’t load the Board Ask. Please try again.',
  dates: (start: string | null, end: string | null, goal: string | null) =>
    [start && end ? `${start} – ${end}` : null, goal ? `${goal} goal` : null]
      .filter((part): part is string => part !== null)
      .join(' · '),
} as const;

/** The director's ask page's query key, under the ask family's own root. */
export const directorAskKey = boardAskKeys.director;

function shortDay(instant: string | null): string | null {
  if (instant === null) return null;
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function money(amount: string | null, cents: 'always' | 'when-present'): string | null {
  if (amount === null) return null;
  const shown = formatMoneyDisplay(amount, { cents });
  return shown === null ? null : `$${shown}`;
}

export function DirectorAskPage() {
  const { board } = useDirectorBoardAddress();
  // A Board Ask is the board's alone (its recipients are board seats).
  if (isCommitteeOnly(board)) return <DirectorBoardOnlyPage testId="page-director-ask" />;
  return <DirectorAskPageForTheBoard />;
}

function DirectorAskPageForTheBoard() {
  const { askId } = useParams({ from: '/director/asks/$askId' });
  const queryClient = useQueryClient();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const [wizard, setWizard] = useState<'create' | 'edit' | null>(null);
  const slots = useAskSlots();
  const key = directorAskKey(orgRef ?? '', askId);

  const read = useQuery({
    queryKey: key,
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: ({ signal }) => fetchDirectorBoardAsk(orgRef ?? '', askId, signal),
  });
  const ask = read.data ?? null;

  const refreshAttention = () =>
    queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });

  // Her first opening, recorded once (the server keeps the first instant).
  const opened = useRef(false);
  useEffect(() => {
    if (ask === null || orgRef === null || ask.openedAt !== null || opened.current) return;
    opened.current = true;
    void markDirectorBoardAskOpened(orgRef, askId).catch(() => undefined);
  }, [ask, orgRef, askId]);

  const act = useMutation({
    mutationFn: (what: 'shared' | 'page_started') =>
      recordDirectorBoardAskAct(orgRef ?? '', askId, what),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      await refreshAttention();
    },
  });

  // A page made elsewhere (the campaign's own public page) is her page all the same: the ask has
  // been acted on, so it leaves her to-do list.
  const pageRecorded = useRef(false);
  const recordAct = act.mutate;
  const pageUnrecorded = ask !== null && ask.page !== null && ask.pageStartedAt === null;
  useEffect(() => {
    if (!pageUnrecorded || pageRecorded.current) return;
    pageRecorded.current = true;
    recordAct('page_started');
  }, [pageUnrecorded, recordAct]);

  const identity = directorBoardIdentity(board);
  if (orgRef === null || notYours || read.isPending || read.isError || ask === null) {
    return (
      <DirectorShell
        title={DIRECTOR_WORDS.pageTitle}
        identity={identity}
        testId="page-director-ask"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {read.isPending ? <Skeleton className="h-90 w-full rounded-lg" /> : null}
          {read.isError ? (
            <Card testId="director-ask-not-found">
              <CardBody>
                <p className={PLAIN}>
                  {isGivmoError(read.error) && read.error.status === 404
                    ? DIRECTOR_WORDS.notFound
                    : DIRECTOR_WORDS.loadFailed}
                </p>
              </CardBody>
            </Card>
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  if (wizard !== null && slots.renderPageWizard !== undefined) {
    return (
      <>
        {slots.renderPageWizard({
          campaignId: ask.fundraiserId,
          ...(wizard === 'edit' && ask.page !== null && ask.page.supporterId !== null
            ? {
                existing: {
                  supporterId: ask.page.supporterId,
                  fundraiserId: ask.page.fundraiserId,
                },
              }
            : {}),
          onBack: () => setWizard(null),
          onExit: () => setWizard(null),
          onPageChanged: () => {
            if (ask.pageStartedAt === null && !pageRecorded.current) {
              pageRecorded.current = true;
              act.mutate('page_started');
            }
            void queryClient.invalidateQueries({ queryKey: key });
          },
        })}
      </>
    );
  }

  const graphics = ask.attachments
    .filter((item) => item.kind === 'image')
    .map((item) => item.collateralItemId);
  const shared = () => {
    if (ask.sharedAt === null) act.mutate('shared');
  };

  return (
    <DirectorShell
      title={ask.campaignName}
      description={DIRECTOR_WORDS.asked(ask.fromDisplayName)}
      identity={identity}
      testId="page-director-ask"
    >
      {/* The container's width, not the window's: the to-do list beside the page narrows it, and
          then the two columns stack. */}
      <div className="@container min-w-0">
        <div className="grid min-w-0 grid-cols-1 items-start gap-20 @[56rem]:grid-cols-[minmax(0,1fr)_439px]">
          <div className="flex min-w-0 flex-col gap-16">
            <CampaignCard ask={ask} />
            <MessageCard ask={ask} />
          </div>
          <div className="flex min-w-0 flex-col gap-16">
            {ask.page !== null ? (
              <YourPageCard
                ask={ask}
                onEdit={() => setWizard('edit')}
                shareKit={slots.renderShareKit?.({
                  campaignId: ask.fundraiserId,
                  collateralItemIds: graphics,
                  bare: true,
                  onShared: shared,
                })}
                pageUrl={slots.pageUrl?.(ask.page.fundraiserId) ?? null}
              />
            ) : (
              <>
                {ask.offersShare &&
                  slots.renderShareKit?.({
                    campaignId: ask.fundraiserId,
                    collateralItemIds: graphics,
                    onShared: shared,
                  })}
                {ask.offersPage && (
                  <Card testId="director-ask-create-page">
                    <CardBody className="flex flex-col items-start gap-14">
                      <span className="flex flex-col gap-4">
                        <span className="font-sans text-s18 font-w600 text-text-strong">
                          {ask.offersShare
                            ? DIRECTOR_WORDS.createTitle
                            : DIRECTOR_WORDS.createTitleOnly}
                        </span>
                        <span className="font-sans text-s15 text-text-secondary">
                          {DIRECTOR_WORDS.createLine}
                        </span>
                      </span>
                      <Button
                        appearance="saas"
                        tone="primary"
                        disabled={slots.renderPageWizard === undefined}
                        onClick={() => setWizard('create')}
                        testId="director-ask-create-your-page"
                      >
                        {DIRECTOR_WORDS.createButton}
                      </Button>
                    </CardBody>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DirectorShell>
  );
}

function CampaignCard({ ask }: { ask: DirectorBoardAsk }) {
  const campaign = ask.campaign;
  const { pageUrl } = useAskSlots();
  const goal = money(campaign?.goalAmount ?? null, 'when-present');
  const line = DIRECTOR_WORDS.dates(
    shortDay(campaign?.publishedAt ?? null),
    shortDay(campaign?.endsAt ?? null),
    goal,
  );
  return (
    <Card flush className="overflow-hidden" testId="director-ask-campaign">
      {campaign?.coverUrl ? (
        <img src={campaign.coverUrl} alt="" className="h-[180px] w-full object-cover" />
      ) : null}
      <CardBody className="flex flex-wrap items-center justify-between gap-12">
        {/* The page's title names the campaign: named once at the top. */}
        <span className={PLAIN}>{line}</span>
        {pageUrl !== undefined && (
          <a
            href={pageUrl(ask.fundraiserId)}
            target="_blank"
            rel="noreferrer"
            className={LINK}
            {...testIdProp('director-ask-see-campaign')}
          >
            {DIRECTOR_WORDS.seeCampaign}
          </a>
        )}
      </CardBody>
    </Card>
  );
}

function MessageCard({ ask }: { ask: DirectorBoardAsk }) {
  const files = ask.attachments.filter((item) => item.kind === 'file');
  return (
    <Card flush testId="director-ask-message">
      <CardHeader
        title={DIRECTOR_WORDS.from(ask.fromDisplayName)}
        subtitle={ask.organizationName ?? undefined}
      />
      <CardBody className="flex flex-col gap-20">
        <p className={BODY}>{ask.message}</p>
        {files.length > 0 && (
          <ul className="flex flex-col divide-y divide-border-subtle border-t border-border-subtle">
            {files.map((file) => {
              const size = boardAskFileSize(file.byteSize);
              return (
                <li
                  key={file.collateralItemId}
                  className="flex items-center gap-12 py-12"
                  {...testIdProp(`director-ask-file-${file.collateralItemId}`)}
                >
                  <span className="flex size-[40px] shrink-0 items-center justify-center rounded-md bg-neutral-100 text-icon">
                    <Icon icon={FileText} size={18} aria-hidden />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-sans text-s14 font-w600 text-text-strong">
                      {file.title}
                    </span>
                    {size !== null && (
                      <span className="font-sans text-s12 text-text-tertiary">{w.pdf(size)}</span>
                    )}
                  </span>
                  <a
                    href={file.url}
                    download={file.fileName}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-6 font-sans text-s14 font-w600 text-brand-600 hover:text-brand-700"
                  >
                    <Icon icon={Download} size={16} aria-hidden />
                    {DIRECTOR_WORDS.download}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function YourPageCard({
  ask,
  onEdit,
  shareKit,
  pageUrl,
}: {
  ask: DirectorBoardAsk;
  onEdit: () => void;
  shareKit: ReactNode;
  pageUrl: string | null;
}) {
  const page = ask.page!;
  const live = page.status === 'Active';
  const raised = money(page.raisedAmount, 'when-present') ?? page.raisedAmount;
  const goal = money(page.goalAmount, 'when-present');
  return (
    <Card flush testId="director-ask-your-page">
      <CardHeader title={DIRECTOR_WORDS.yourPage} subtitle={DIRECTOR_WORDS.yourPageLine} />
      <CardBody className="flex flex-col gap-10">
        <p className="font-sans text-s14 text-text-secondary">
          <span className="font-sans text-s20 font-w700 text-text-strong">{raised}</span>{' '}
          {DIRECTOR_WORDS.raisedOf(goal, page.donorCount)}
        </p>
        {page.goalAmount !== null && (
          <Meter
            value={Number(page.raisedAmount)}
            max={Math.max(Number(page.goalAmount), 1)}
            size="sm"
            aria-label={DIRECTOR_WORDS.yourPage}
          />
        )}
        <div className="flex flex-wrap items-center gap-14">
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={Pencil} size={14} aria-hidden />}
            onClick={onEdit}
            testId="director-ask-edit-page"
          >
            {live ? DIRECTOR_WORDS.editPage : DIRECTOR_WORDS.finishPage}
          </Button>
          {live && pageUrl !== null && (
            <a
              href={pageUrl}
              target="_blank"
              rel="noreferrer"
              className={LINK}
              {...testIdProp('director-ask-see-page')}
            >
              {DIRECTOR_WORDS.seePage}
            </a>
          )}
        </div>
        {(live || ask.offersShare) && shareKit ? (
          <div className="border-t border-border-subtle pt-16">{shareKit}</div>
        ) : null}
      </CardBody>
    </Card>
  );
}
